import { useCallback, useState } from 'react'
import IfGlobalOrg from '@/components/organizations/IfGlobalOrg'
import { AdminPageHeader } from '@/components/patterns/admin-page'
import { InfoNotice } from '@/components/patterns/info-notice'
import StripeConnectDashboardButton from '@/components/stripe-connect/StripeConnectDashboardButton'
import StripeConnectPayoutManager from '@/components/stripe-connect/StripeConnectPayoutManager'
import { canUseStripeConnectOnboarding } from '@/components/stripe-connect/stripeConnectStatus'
import { STRIPE_CONNECT_PAYOUT_SECTION } from '@/components/stripe-connect/stripeConnectSurfaceContent'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { usePathHelper } from '@/hooks/usePathHelper'
import { ConnectAccountStatus } from '@/lib/enums/ConnectAccountStatus'
import { Can } from '@/lib/casl'
import { stripeHubLink, stripeOnboardingLink } from '@/lib/stripe-financial-routes'
import { assertNotSalesDemoOrganization } from '@/lib/services/sales-demos/sales-demos.guard'
import {
  stripeConnectQueries,
  useStripeConnectStatus,
} from '@/lib/services/stripe-connect/stripe-connect.query'
import { Typography } from '@/components/ui/typography'
import {
  createFileRoute,
  Link,
  notFound,
  redirect,
  useNavigate,
  useParams,
} from '@tanstack/react-router'
import { AlertCircleIcon, Landmark } from 'lucide-react'

export const Route = createFileRoute('/_authenticated/$orgId/settings/stripe/payouts')({
  component: SettingsPayoutsPage,
  loader: async ({ context, params }) => {
    if (!context.ability.can('edit', 'stripe-account')) {
      throw notFound()
    }

    await assertNotSalesDemoOrganization(context.queryClient, params.orgId)

    const stripeConnectStatus = await context.queryClient.fetchQuery(
      stripeConnectQueries.status({ organizationId: params.orgId, refresh: true })
    )

    if (canUseStripeConnectOnboarding(stripeConnectStatus?.status)) {
      throw redirect(stripeOnboardingLink(params.orgId))
    }
  },
})

function StripePayoutsEmbeddedContent({ orgId, stripeStatus, onSessionConflict }) {
  const path = usePathHelper()

  return (
    <div className="space-y-6">
      <AdminPageHeader
        icon={Landmark}
        title="Payouts"
        description={STRIPE_CONNECT_PAYOUT_SECTION.description}
        actions={
          stripeStatus?.status === ConnectAccountStatus.Verified.Value ? (
            <StripeConnectDashboardButton />
          ) : null
        }
      />

      <Can I="view" a="payouts">
        <InfoNotice compact tone="neutral" title="Looking for deposit history?">
          <div className="space-y-2">
            <p>
              Use the Credit Card Deposit Report to see which orders make up each deposit, review
              deposit history, and download spreadsheets. Use this page to update your bank details
              and deposit schedule.
            </p>
            <Link
              {...path.deposits.index().using({ orgId }).link}
              className="inline-flex font-medium text-interaction underline-offset-2 hover:underline"
            >
              Open Credit Card Deposit Report
            </Link>
          </div>
        </InfoNotice>
      </Can>

      <StripeConnectPayoutManager
        key={orgId}
        organizationId={orgId}
        onSessionConflict={onSessionConflict}
        showSectionHeader={false}
      />
    </div>
  )
}

function SettingsPayoutsContent({ orgId }) {
  const navigate = useNavigate()
  const [sessionStatusOverride, setSessionStatusOverride] = useState(null)
  const { data: stripeConnectStatus } = useStripeConnectStatus({
    refresh: true,
    refetchOnMount: false,
  })
  const effectiveStripeStatus = sessionStatusOverride ?? stripeConnectStatus
  const status = effectiveStripeStatus?.status

  const handleSessionConflict = useCallback(
    (nextStatus) => {
      if (canUseStripeConnectOnboarding(nextStatus?.status)) {
        void navigate({
          ...stripeOnboardingLink(orgId),
          replace: true,
        })
        return
      }

      setSessionStatusOverride(nextStatus)
    },
    [navigate, orgId]
  )

  return status === ConnectAccountStatus.Verified.Value ? (
    <StripePayoutsEmbeddedContent
      orgId={orgId}
      stripeStatus={effectiveStripeStatus}
      onSessionConflict={handleSessionConflict}
    />
  ) : (
    <div className="space-y-6">
      <AdminPageHeader
        icon={Landmark}
        title="Payouts"
        description={STRIPE_CONNECT_PAYOUT_SECTION.description}
      />

      <Alert variant="info">
        <AlertCircleIcon />
        <AlertTitle>Payouts are not available yet</AlertTitle>
        <AlertDescription className="space-y-3">
          <Typography variant="label" className="block">
            Open Stripe to review account alerts, verification status, and any follow-up details
            from Stripe.
          </Typography>
          <Button asChild variant="outline">
            <Link {...stripeHubLink(orgId)}>Go to Stripe</Link>
          </Button>
        </AlertDescription>
      </Alert>
    </div>
  )
}

export function SettingsPayoutsPage() {
  const { orgId } = useParams({ strict: false })

  return (
    <div className="space-y-6">
      <IfGlobalOrg
        show={false}
        fallback={
          <Alert variant="info">
            <AlertCircleIcon />
            <AlertTitle>This is the global org payouts page</AlertTitle>
            <AlertDescription>Do not set up a Stripe connect account.</AlertDescription>
          </Alert>
        }
      >
        <SettingsPayoutsContent key={orgId ?? 'unknown-org'} orgId={orgId} />
      </IfGlobalOrg>
    </div>
  )
}
